import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  X, 
  RefreshCw, 
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Eye,
  Calendar as CalendarIcon,
  BookOpen,
  TrendingDown,
  Package,
  Truck,
  Building,
  Monitor,
  Car,
  Sofa,
  DollarSign,
  FileText,
  History,
  AlertTriangle,
  Check
} from "lucide-react";
import axios from "axios";
import { format, addMonths } from "date-fns";
import { sv } from "date-fns/locale";

const FixedAssets = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailAsset, setDetailAsset] = useState(null);
  const [depreciationHistory, setDepreciationHistory] = useState([]);
  const [depDialogOpen, setDepDialogOpen] = useState(false);
  const [selectedAssetForDep, setSelectedAssetForDep] = useState(null);
  const [depPeriod, setDepPeriod] = useState(format(new Date(), "yyyy-MM"));
  
  // Multi-select
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    asset_number: "",
    category: "equipment",
    purchase_date: format(new Date(), "yyyy-MM-dd"),
    supplier_id: "",
    invoice_reference: "",
    purchase_price: 0,
    location: "",
    serial_number: "",
    depreciation_method: "linear",
    useful_life_months: 60,
    residual_value: 0,
    depreciation_account: "7830",
    asset_account: "1220",
    accumulated_depreciation_account: "1229",
    notes: "",
  });

  const categoryOptions = [
    { value: "equipment", label: t('fixedAssets.categories.equipment'), icon: Package },
    { value: "vehicles", label: t('fixedAssets.categories.vehicles'), icon: Car },
    { value: "buildings", label: t('fixedAssets.categories.buildings'), icon: Building },
    { value: "furniture", label: t('fixedAssets.categories.furniture'), icon: Sofa },
    { value: "it_equipment", label: t('fixedAssets.categories.itEquipment'), icon: Monitor },
    { value: "other", label: t('fixedAssets.categories.other'), icon: Package },
  ];

  const accountOptions = [
    { value: "1210", label: "1210 - Byggnader" },
    { value: "1220", label: "1220 - Maskiner och inventarier" },
    { value: "1230", label: "1230 - Installationer" },
    { value: "1240", label: "1240 - Bilar och transportmedel" },
    { value: "1250", label: "1250 - Datorer" },
    { value: "1260", label: "1260 - Övriga maskiner" },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assetsRes, suppliersRes, statsRes] = await Promise.all([
        axios.get(`${API}/fixed-assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/fixed-assets/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAssets(assetsRes.data);
      setSuppliers(suppliersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t('fixedAssets.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await axios.put(`${API}/fixed-assets/${editingAsset.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('fixedAssets.updated'));
      } else {
        await axios.post(`${API}/fixed-assets`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('fixedAssets.created'));
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fixedAssets.saveFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('fixedAssets.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/fixed-assets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('fixedAssets.deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('fixedAssets.deleteFailed'));
    }
  };

  const handleBookPurchase = async (asset) => {
    if (asset.booked) {
      toast.info(t('fixedAssets.alreadyBooked'));
      return;
    }
    try {
      const res = await axios.post(`${API}/fixed-assets/${asset.id}/book-purchase`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('fixedAssets.purchaseBooked', { number: res.data.entry_number }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fixedAssets.bookFailed'));
    }
  };

  const openDepreciationDialog = (asset) => {
    setSelectedAssetForDep(asset);
    setDepPeriod(format(new Date(), "yyyy-MM"));
    setDepDialogOpen(true);
  };

  const handleBookDepreciation = async () => {
    if (!selectedAssetForDep) return;
    try {
      const res = await axios.post(
        `${API}/fixed-assets/${selectedAssetForDep.id}/book-depreciation?period=${depPeriod}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('fixedAssets.depreciationBooked', { 
        period: depPeriod, 
        amount: formatCurrency(res.data.amount) 
      }));
      setDepDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fixedAssets.depreciationFailed'));
    }
  };

  const fetchDepreciationHistory = async (assetId) => {
    try {
      const res = await axios.get(`${API}/fixed-assets/${assetId}/depreciation-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepreciationHistory(res.data);
    } catch (error) {
      setDepreciationHistory([]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      asset_number: "",
      category: "equipment",
      purchase_date: format(new Date(), "yyyy-MM-dd"),
      supplier_id: "",
      invoice_reference: "",
      purchase_price: 0,
      location: "",
      serial_number: "",
      depreciation_method: "linear",
      useful_life_months: 60,
      residual_value: 0,
      depreciation_account: "7830",
      asset_account: "1220",
      accumulated_depreciation_account: "1229",
      notes: "",
    });
    setEditingAsset(null);
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      name: asset.name || "",
      description: asset.description || "",
      asset_number: asset.asset_number || "",
      category: asset.category || "equipment",
      purchase_date: asset.purchase_date || format(new Date(), "yyyy-MM-dd"),
      supplier_id: asset.supplier_id || "",
      invoice_reference: asset.invoice_reference || "",
      purchase_price: asset.purchase_price || 0,
      location: asset.location || "",
      serial_number: asset.serial_number || "",
      depreciation_method: asset.depreciation_method || "linear",
      useful_life_months: asset.useful_life_months || 60,
      residual_value: asset.residual_value || 0,
      depreciation_account: asset.depreciation_account || "7830",
      asset_account: asset.asset_account || "1220",
      accumulated_depreciation_account: asset.accumulated_depreciation_account || "1229",
      notes: asset.notes || "",
    });
    setDialogOpen(true);
  };

  const openDetailDialog = async (asset) => {
    setDetailAsset(asset);
    await fetchDepreciationHistory(asset.id);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 opacity-50" />;
    return sortDirection === "asc" ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedAssets.size === filteredAndSortedAssets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(filteredAndSortedAssets.map(a => a.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedAssets(newSelected);
  };

  // Filter and sort
  const filteredAndSortedAssets = useMemo(() => {
    let result = [...assets];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(a => 
        a.name?.toLowerCase().includes(term) ||
        a.asset_number?.toLowerCase().includes(term) ||
        a.serial_number?.toLowerCase().includes(term) ||
        a.location?.toLowerCase().includes(term)
      );
    }
    
    if (filterStatus !== "all") {
      result = result.filter(a => a.status === filterStatus);
    }
    
    if (filterCategory !== "all") {
      result = result.filter(a => a.category === filterCategory);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (typeof aVal === "string") aVal = aVal?.toLowerCase() || "";
      if (typeof bVal === "string") bVal = bVal?.toLowerCase() || "";
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [assets, searchTerm, filterStatus, filterCategory, sortColumn, sortDirection]);

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-green-100 text-green-700">{t('fixedAssets.status.active')}</Badge>,
      fully_depreciated: <Badge className="bg-blue-100 text-blue-700">{t('fixedAssets.status.fullyDepreciated')}</Badge>,
      disposed: <Badge className="bg-slate-100 text-slate-700">{t('fixedAssets.status.disposed')}</Badge>
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      equipment: Package,
      vehicles: Car,
      buildings: Building,
      furniture: Sofa,
      it_equipment: Monitor,
      other: Package
    };
    const Icon = icons[category] || Package;
    return <Icon size={16} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="fixed-assets">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('fixedAssets.title')}</h1>
          <p className="text-slate-500 mt-1">{assets.length} {t('fixedAssets.registered')}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw size={16} className="mr-2" />
            {t('common.refresh')}
          </Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="new-asset-btn"
          >
            <Plus size={16} className="mr-2" />
            {t('fixedAssets.newAsset')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('fixedAssets.stats.purchaseValue')}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_purchase_value)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <DollarSign className="text-indigo-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('fixedAssets.stats.bookValue')}</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_book_value)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileText className="text-green-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('fixedAssets.stats.accumulated')}</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.total_accumulated_depreciation)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingDown className="text-amber-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('fixedAssets.stats.activeCount')}</p>
                <p className="text-2xl font-bold">{stats.active_count} / {stats.total_count}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="text-blue-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder={t('fixedAssets.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-assets"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="active">{t('fixedAssets.status.active')}</SelectItem>
                <SelectItem value="fully_depreciated">{t('fixedAssets.status.fullyDepreciated')}</SelectItem>
                <SelectItem value="disposed">{t('fixedAssets.status.disposed')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t('fixedAssets.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {categoryOptions.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card className="card-base overflow-hidden" data-testid="assets-table">
        <CardContent className="p-0">
          {filteredAndSortedAssets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedAssets.size === filteredAndSortedAssets.length && filteredAndSortedAssets.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3 w-20">
                      <button onClick={() => handleSort("asset_number")} className="flex items-center hover:text-slate-700">
                        Nr <SortIcon column="asset_number" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button onClick={() => handleSort("name")} className="flex items-center hover:text-slate-700">
                        {t('fixedAssets.name')} <SortIcon column="name" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3 w-28">
                      {t('fixedAssets.category')}
                    </th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-3 w-28">
                      {t('fixedAssets.purchasePrice')}
                    </th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-3 w-28">
                      {t('fixedAssets.bookValue')}
                    </th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-3 w-32">
                      {t('fixedAssets.depreciation')}
                    </th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-3 w-24">
                      {t('common.status')}
                    </th>
                    <th className="w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedAssets.map((asset) => (
                    <tr 
                      key={asset.id} 
                      className="border-b hover:bg-slate-50 transition-colors"
                      data-testid={`asset-row-${asset.id}`}
                    >
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedAssets.has(asset.id)}
                          onCheckedChange={() => toggleSelect(asset.id)}
                        />
                      </td>
                      <td className="p-3 font-mono text-sm text-indigo-600 font-medium">
                        {asset.asset_number}
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-slate-900">{asset.name}</p>
                          {asset.serial_number && (
                            <p className="text-xs text-slate-500">SN: {asset.serial_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          {getCategoryIcon(asset.category)}
                          <span>{categoryOptions.find(c => c.value === asset.category)?.label || asset.category}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono text-sm">
                        {formatCurrency(asset.purchase_price)}
                      </td>
                      <td className="p-3 text-right font-mono text-sm font-medium text-green-600">
                        {formatCurrency(asset.book_value)}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col items-center gap-1">
                          <Progress value={asset.depreciation_percentage} className="h-2 w-20" />
                          <span className="text-xs text-slate-500">{Math.round(asset.depreciation_percentage)}%</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {getStatusBadge(asset.status)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openDetailDialog(asset)} title={t('common.view')}>
                            <Eye size={14} />
                          </Button>
                          {!asset.booked && (
                            <Button variant="ghost" size="sm" onClick={() => handleBookPurchase(asset)} title={t('fixedAssets.bookPurchase')} className="text-blue-600">
                              <BookOpen size={14} />
                            </Button>
                          )}
                          {asset.status === "active" && (
                            <Button variant="ghost" size="sm" onClick={() => openDepreciationDialog(asset)} title={t('fixedAssets.bookDepreciation')} className="text-amber-600">
                              <TrendingDown size={14} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(asset)} title={t('common.edit')}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(asset.id)} title={t('common.delete')}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Package className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">{t('fixedAssets.noAssets')}</p>
              <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={() => { resetForm(); setDialogOpen(true); }}>
                <Plus size={16} className="mr-2" />
                {t('fixedAssets.addFirst')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={20} />
              {editingAsset ? t('fixedAssets.editAsset') : t('fixedAssets.newAsset')}
            </DialogTitle>
            <DialogDescription>
              {editingAsset ? t('fixedAssets.editDescription') : t('fixedAssets.createDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="basic">{t('fixedAssets.tabs.basic')}</TabsTrigger>
              <TabsTrigger value="depreciation">{t('fixedAssets.tabs.depreciation')}</TabsTrigger>
              <TabsTrigger value="other">{t('fixedAssets.tabs.other')}</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit}>
              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.name')} *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('fixedAssets.namePlaceholder')}
                      required
                      data-testid="asset-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.assetNumber')}</Label>
                    <Input
                      value={formData.asset_number}
                      onChange={(e) => setFormData({ ...formData, asset_number: e.target.value })}
                      placeholder="AT1"
                      data-testid="asset-number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.category')}</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.purchaseDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.purchase_date}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={new Date(formData.purchase_date)}
                          onSelect={(date) => date && setFormData({ ...formData, purchase_date: format(date, "yyyy-MM-dd") })}
                          locale={sv}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.supplier')}</Label>
                    <Select value={formData.supplier_id || "none"} onValueChange={(v) => setFormData({ ...formData, supplier_id: v === "none" ? "" : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('fixedAssets.selectSupplier')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('common.none')}</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.invoiceReference')}</Label>
                    <Input
                      value={formData.invoice_reference}
                      onChange={(e) => setFormData({ ...formData, invoice_reference: e.target.value })}
                      placeholder="F-12345"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('fixedAssets.purchasePrice')} *</Label>
                  <Input
                    type="number"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    required
                    data-testid="purchase-price-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('fixedAssets.description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('fixedAssets.descriptionPlaceholder')}
                    rows={2}
                  />
                </div>
              </TabsContent>

              <TabsContent value="depreciation" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.depreciationMethod')}</Label>
                    <Select value={formData.depreciation_method} onValueChange={(v) => setFormData({ ...formData, depreciation_method: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="linear">{t('fixedAssets.methods.linear')}</SelectItem>
                        <SelectItem value="declining_balance">{t('fixedAssets.methods.decliningBalance')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.usefulLife')} ({t('fixedAssets.months')})</Label>
                    <Input
                      type="number"
                      value={formData.useful_life_months}
                      onChange={(e) => setFormData({ ...formData, useful_life_months: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('fixedAssets.residualValue')}</Label>
                  <Input
                    type="number"
                    value={formData.residual_value}
                    onChange={(e) => setFormData({ ...formData, residual_value: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                  />
                </div>

                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <h4 className="font-medium">{t('fixedAssets.accounts')}</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('fixedAssets.assetAccount')}</Label>
                      <Input
                        value={formData.asset_account}
                        onChange={(e) => setFormData({ ...formData, asset_account: e.target.value })}
                        placeholder="1220"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('fixedAssets.depreciationAccount')}</Label>
                      <Input
                        value={formData.depreciation_account}
                        onChange={(e) => setFormData({ ...formData, depreciation_account: e.target.value })}
                        placeholder="7830"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('fixedAssets.accumulatedAccount')}</Label>
                      <Input
                        value={formData.accumulated_depreciation_account}
                        onChange={(e) => setFormData({ ...formData, accumulated_depreciation_account: e.target.value })}
                        placeholder="1229"
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="other" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.location')}</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder={t('fixedAssets.locationPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fixedAssets.serialNumber')}</Label>
                    <Input
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      placeholder="SN-12345"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('fixedAssets.notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('fixedAssets.notesPlaceholder')}
                    rows={3}
                  />
                </div>
              </TabsContent>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-asset-btn">
                  {editingAsset ? t('common.save') : t('fixedAssets.createAsset')}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Depreciation Dialog */}
      <Dialog open={depDialogOpen} onOpenChange={setDepDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown size={20} />
              {t('fixedAssets.bookDepreciation')}
            </DialogTitle>
            <DialogDescription>
              {selectedAssetForDep?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{t('fixedAssets.monthlyDepreciation')}:</span>
                <span className="font-mono font-medium">{formatCurrency(selectedAssetForDep?.monthly_depreciation)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{t('fixedAssets.currentBookValue')}:</span>
                <span className="font-mono font-medium text-green-600">{formatCurrency(selectedAssetForDep?.book_value)}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('fixedAssets.period')}</Label>
              <Input
                type="month"
                value={depPeriod}
                onChange={(e) => setDepPeriod(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDepDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleBookDepreciation} className="bg-amber-600 hover:bg-amber-700">
              <Check size={14} className="mr-2" />
              {t('fixedAssets.bookDepreciationBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailAsset} onOpenChange={(open) => !open && setDetailAsset(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getCategoryIcon(detailAsset?.category)}
              {detailAsset?.name}
            </DialogTitle>
          </DialogHeader>
          
          {detailAsset && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">{t('fixedAssets.purchasePrice')}</p>
                  <p className="text-xl font-bold">{formatCurrency(detailAsset.purchase_price)}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-slate-500">{t('fixedAssets.bookValue')}</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(detailAsset.book_value)}</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-slate-500">{t('fixedAssets.accumulated')}</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(detailAsset.accumulated_depreciation)}</p>
                </div>
              </div>

              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('fixedAssets.depreciationProgress')}</span>
                  <span className="font-medium">{Math.round(detailAsset.depreciation_percentage)}%</span>
                </div>
                <Progress value={detailAsset.depreciation_percentage} className="h-3" />
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">{t('fixedAssets.assetNumber')}</p>
                  <p className="font-mono font-medium">{detailAsset.asset_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t('fixedAssets.purchaseDate')}</p>
                  <p className="font-medium">{detailAsset.purchase_date}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t('fixedAssets.usefulLife')}</p>
                  <p className="font-medium">{detailAsset.useful_life_months} {t('fixedAssets.months')}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t('fixedAssets.monthlyDepreciation')}</p>
                  <p className="font-mono font-medium">{formatCurrency(detailAsset.monthly_depreciation)}</p>
                </div>
                {detailAsset.supplier_name && (
                  <div>
                    <p className="text-slate-500">{t('fixedAssets.supplier')}</p>
                    <p className="font-medium">{detailAsset.supplier_name}</p>
                  </div>
                )}
                {detailAsset.location && (
                  <div>
                    <p className="text-slate-500">{t('fixedAssets.location')}</p>
                    <p className="font-medium">{detailAsset.location}</p>
                  </div>
                )}
              </div>

              {/* Depreciation History */}
              {depreciationHistory.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <History size={16} />
                    {t('fixedAssets.depreciationHistory')}
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2">{t('fixedAssets.period')}</th>
                          <th className="text-right p-2">{t('fixedAssets.amount')}</th>
                          <th className="text-right p-2">{t('fixedAssets.accumulated')}</th>
                          <th className="text-right p-2">{t('fixedAssets.bookValue')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depreciationHistory.map((entry, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{entry.period}</td>
                            <td className="p-2 text-right font-mono">{formatCurrency(entry.amount)}</td>
                            <td className="p-2 text-right font-mono">{formatCurrency(entry.accumulated_after)}</td>
                            <td className="p-2 text-right font-mono text-green-600">{formatCurrency(entry.book_value_after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailAsset(null)}>
                  {t('common.close')}
                </Button>
                <Button onClick={() => { handleEdit(detailAsset); setDetailAsset(null); }}>
                  <Edit size={14} className="mr-2" />
                  {t('common.edit')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FixedAssets;
